import cn from 'classnames';
import { useTheme } from 'styled-components';

import { IConDefaultType } from '..';

export enum DefaultUserIconVariant {
  DEFAULT = 'default',
  NO_BORDER = 'no_border',
  CHAT_SIDE_MENU = 'chat_side_menu',
}

interface IDefaultUserIconProps extends IConDefaultType {
  height?: number;
  variant?: DefaultUserIconVariant;
}

function DefaultUserIcon({
  color,
  className,
  size = 40,
  height,
  rotate,
  flip,
  variant = DefaultUserIconVariant.DEFAULT,
  style,
}: IDefaultUserIconProps) {
  const theme = useTheme();
  return (
    <i
      className={cn('dds-icon', className, { flip })}
      style={{
        width: size,
        minWidth: size,
        height: height ?? size,
        transform: `rotate(${rotate}deg)`,
        cursor: 'default',
        ...style,
      }}
    >
      {variant === DefaultUserIconVariant.DEFAULT && (
        <svg viewBox="0 0 40 41" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="default">
            <g clipPath="url(#clip0_13424_249989)">
              <rect y="0.503906" width="40" height="40" rx="20" fill={color || theme.color.popup.background} />
              <g id="Shape">
                <mask
                  id="mask0_13424_249989"
                  style={{ maskType: 'alpha' }}
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="40"
                  height="41"
                >
                  <circle id="Ellipse 59" cx="20" cy="20.5039" r="20" fill={color || theme.color.popup.background} />
                </mask>
                <g mask="url(#mask0_13424_249989)">
                  <g id="Union">
                    <path
                      d="M19.9989 28.3239C25.0704 28.3239 29.1969 23.3662 29.1969 17.271C29.1969 11.6937 25.5867 8.08984 19.9989 8.08984C14.4125 8.08984 10.8023 11.6937 10.8023 17.271C10.8023 23.366 14.9285 28.3239 19.9989 28.3239Z"
                      fill="#D8D8D8"
                    />
                    <path
                      d="M16.4622 31.083C8.64752 31.083 2.3125 37.4181 2.3125 45.2327V53.015C2.3125 56.9223 5.48001 60.0898 9.38733 60.0898H30.6118C34.5191 60.0898 37.6867 56.9223 37.6867 53.015V45.2327C37.6867 37.4181 31.3516 31.083 23.537 31.083H16.4622Z"
                      fill="#D8D8D8"
                    />
                  </g>
                </g>
              </g>
            </g>
            <rect x="0.5" y="1.00391" width="39" height="39" rx="19.5" stroke={color || theme.color.line.line_2} />
          </g>
          <defs>
            <clipPath id="clip0_13424_249989">
              <rect y="0.503906" width="40" height="40" rx="20" fill={color || theme.color.popup.background} />
            </clipPath>
          </defs>
        </svg>
      )}
      {variant === DefaultUserIconVariant.NO_BORDER && (
        <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_777_38885)">
            <rect width="40" height="40" fill={color || theme.color.popup.background} />
            <path
              d="M40 19.5122C40 8.73591 31.0457 0 20 0C8.9543 0 0 8.73591 0 19.5122C0 30.2885 8.9543 39.0244 20 39.0244C31.0457 39.0244 40 30.2885 40 19.5122Z"
              fill={color || theme.color.popup.background}
            />
            <path
              d="M20 39.0244C31.0457 39.0244 40 30.2885 40 19.5122C40 8.73591 31.0457 0 20 0C8.9543 0 0 8.73591 0 19.5122C0 30.2885 8.9543 39.0244 20 39.0244Z"
              fill={color || theme.color.popup.background}
            />
            <path
              d="M20.0013 27.139C25.0728 27.139 29.1993 22.3022 29.1993 16.3557C29.1993 10.9144 25.5891 7.39844 20.0013 7.39844C14.4149 7.39844 10.8047 10.9144 10.8047 16.3557C10.8047 22.302 14.9309 27.139 20.0013 27.139Z"
              fill="#D8D8D8"
            />
            <path
              d="M16.4622 29.8281C8.64752 29.8281 2.3125 36.0087 2.3125 43.6327V51.2252C2.3125 55.0372 5.48001 58.1274 9.38733 58.1274H30.6118C34.5191 58.1274 37.6867 55.0372 37.6867 51.2252V43.6327C37.6867 36.0087 31.3516 29.8281 23.537 29.8281H16.4622Z"
              fill="#D8D8D8"
            />
          </g>
          <defs>
            <clipPath id="clip0_777_38885">
              <rect width="40" height="40" fill={color || theme.color.popup.background} />
            </clipPath>
          </defs>
        </svg>
      )}
      {variant === DefaultUserIconVariant.CHAT_SIDE_MENU && (
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
          <g clipPath="url(#clip0_2200_360839)">
            <rect width="48" height="48" rx="24" fill={theme.color.field.background} />
            <mask
              id="mask0_2200_360839"
              style={{ maskType: 'luminance' }}
              maskUnits="userSpaceOnUse"
              x="-2"
              y="5"
              width="52"
              height="48"
            >
              <rect
                width="51.072"
                height="46.9533"
                rx="6.17806"
                transform="matrix(-1 0 0 1 49.5371 5.69141)"
                fill="white"
              />
            </mask>
            <g mask="url(#mask0_2200_360839)">
              <path
                d="M23.999 32.5454C29.9039 32.5454 34.7084 26.7731 34.7084 19.6763C34.7084 13.1825 30.505 8.98639 23.999 8.98639C17.4946 8.98639 13.2912 13.1825 13.2912 19.6763C13.2912 26.7728 18.0955 32.5454 23.999 32.5454Z"
                fill={theme.color.line.line_3}
              />
              <path
                d="M19.8811 35.758C10.7823 35.758 3.40625 43.134 3.40625 52.2328V61.294C3.40625 65.8434 7.09427 69.5314 11.6437 69.5314H36.3559C40.9053 69.5314 44.5933 65.8434 44.5933 61.294V52.2328C44.5933 43.134 37.2173 35.758 28.1185 35.758H19.8811Z"
                fill={theme.color.line.line_3}
              />
            </g>
          </g>
          <rect x="0.5" y="0.5" width="47" height="47" rx="23.5" stroke={theme.color.line.line_2} />
          <defs>
            <clipPath id="clip0_2200_360839">
              <rect width="48" height="48" rx="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      )}
    </i>
  );
}

export default DefaultUserIcon;
